import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Input, Button } from 'antd';
import { AlertOutlined } from '@ant-design/icons';

const { TextArea } = Input;

// Shared with the escalation activity modal on the panels.
export const THEME = {
  primary: "#4e7661",
  light: "#eef4f1",
  border: "#c5d9ce",
};
export const MAX_TEXT_LENGTH = 1000;

// Shared by the escalate buttons on the order detail page and the resolve / close
// action on the escalation panels. onConfirm(text) resolves to { success, message }.
const EscalationModal = ({
  open,
  loading,
  title,
  subtitle,
  description,
  placeholder,
  textRequired,
  confirmLabel,
  onCancel,
  onConfirm,
}) => {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setText('');
      setResult(null);
    }
  }, [open]);

  const succeeded = result?.success === true;
  const canSubmit = !textRequired || text.trim().length > 0;

  const handleConfirm = async () => {
    const response = await onConfirm(text.trim());
    if (response) setResult(response);
  };

  const handleClose = () => {
    if (loading) return;
    setResult(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: THEME.light,
              border: `1px solid ${THEME.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AlertOutlined style={{ color: THEME.primary, fontSize: 16 }} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>{subtitle}</div>
            )}
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      maskClosable={!loading}
      closable={!loading}
      destroyOnClose
    >
      <div style={{ padding: "4px 0 8px" }}>
        {description && (
          <p style={{ fontSize: 13, color: "#666", lineHeight: 1.55, margin: "0 0 12px" }}>
            {description}
          </p>
        )}
        {!succeeded && (
          <TextArea
            rows={4}
            value={text}
            maxLength={MAX_TEXT_LENGTH}
            showCount
            placeholder={placeholder}
            disabled={loading}
            onChange={(e) => setText(e.target.value)}
            style={{ marginBottom: 16 }}
          />
        )}
        {result && (
          <div
            style={{
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 16,
              background: succeeded ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${succeeded ? "#b7eb8f" : "#ffccc7"}`,
              color: succeeded ? "#389e0d" : "#cf1322",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {result.message}
          </div>
        )}
        {succeeded ? (
          <Button
            type="primary"
            block
            size="large"
            onClick={handleClose}
            style={{ background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 44 }}
          >
            Done
          </Button>
        ) : (
          <Button
            type="primary"
            block
            size="large"
            icon={<AlertOutlined />}
            loading={loading}
            disabled={!canSubmit}
            onClick={handleConfirm}
            style={canSubmit ? { background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 44 } : { fontWeight: 600, height: 44 }}
          >
            {confirmLabel}
          </Button>
        )}
      </div>
    </Modal>
  );
};

EscalationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  placeholder: PropTypes.string,
  textRequired: PropTypes.bool,
  confirmLabel: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default EscalationModal;
